/**
 * CDN Service — Global singleton for fetching resources from CDN (R2-backed)
 *
 * This is the core service used across the entire app to:
 *  1. Resolve CDN URLs for public items (with automatic re-push on expiry)
 *  2. Obtain signed URLs for private items via a backend call
 *  3. Manage a concurrency-limited priority queue (prevents browser overload)
 *  4. Maintain an in-memory LRU cache for instant re-access
 *  5. Decompress gzip-compressed files automatically (DecompressionStream + fallback)
 *
 * CDN Base: https://media.eduexpertai.com
 * Django model: Media_File_Table (compression_method field drives decompression)
 *
 * Compression handling:
 *  - 'gzip'  → fetched as ArrayBuffer → decompressed → blob: URL returned
 *  - 'webp'  → browser handles natively (no decompression needed)
 *  - 'video' → browser codec handles (no decompression needed)
 *  - 'audio' → browser codec handles (no decompression needed)
 *  - 'none'/null → served as-is
 *
 * Android / Mobile support:
 *  - All APIs used (fetch, Map, Promise.allSettled, AbortController) work on
 *    Android Chrome 76+, Samsung Internet 12+, Firefox Android 68+.
 *  - DecompressionStream: Chrome 80+ / Edge 80+ (Android Chrome auto-updates).
 *    For older browsers, falls back gracefully to streaming via Response.
 *
 * Related files:
 *  - client/services/cdnService.types.ts  — TypeScript interfaces
 *  - client/hooks/useCDNResource.ts       — React hook for a single item
 *  - client/hooks/useCDNBatch.ts          — React hook for batch/progressive loading
 *
 * Usage:
 *   import { cdnService } from '@/services/cdnService';
 *   const resource = await cdnService.fetch(itemMeta);
 */

import axios from 'axios';
import baseURL from '@/http';
import type {
  CDNItemMeta,
  CDNFetchOptions,
  CDNResolvedResource,
  CDNFetchError,
  CDNCacheEntry,
  CDNServiceConfig,
  CDNQueueItem,
  CompressionMethod,
  CDNDataType,
} from './cdnService.types';

// ════════════════════════════════════════════════════════════════
// Default configuration
// ════════════════════════════════════════════════════════════════

const DEFAULT_CONFIG: CDNServiceConfig = {
  cdnBaseUrl: 'https://media.eduexpertai.com',
  apiBaseUrl: undefined, // falls back to http.tsx baseURL
  defaultPublicApiEndpoint: '/media/refresh',
  defaultPrivateApiEndpoint: '/media/signed-url',
  maxConcurrency: 8,
  publicCacheTTL: 30 * 60 * 1000,       // 30 minutes
  privateCacheTTL: 0,                     // never cache signed URLs by default
  maxCacheSize: 10_000,
  debug: import.meta.env.DEV,
};

// ════════════════════════════════════════════════════════════════
// CDN Service Class
// ════════════════════════════════════════════════════════════════

class CDNService {
  private config: CDNServiceConfig;
  private cache = new Map<string, CDNCacheEntry>();
  private cacheAccessOrder: string[] = []; // for LRU eviction
  private queue: CDNQueueItem[] = [];
  private activeCount = 0;
  private inflightRequests = new Map<string, Promise<CDNResolvedResource>>();

  // ── Listeners for batch progress tracking ──
  private listeners = new Set<(itemId: string | number, result: CDNResolvedResource | CDNFetchError) => void>();

  constructor(config?: Partial<CDNServiceConfig>) {
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // ────────────────────────────────────────────
  // Public API
  // ────────────────────────────────────────────

  /**
   * Fetch a single CDN resource. Returns a resolved URL.
   * If cached and still valid, returns instantly.
   * Otherwise queues a fetch with the given priority.
   */
  fetch(
    meta: CDNItemMeta,
    options: CDNFetchOptions = {},
  ): Promise<CDNResolvedResource> {
    const cacheKey = this.buildCacheKey(meta);

    // 1. Check in-memory cache
    const cached = this.cache.get(cacheKey);
    if (cached && !this.isCacheExpired(cached)) {
      this.touchCacheKey(cacheKey);
      this.log(`Cache HIT: ${cacheKey}`);
      return Promise.resolve({
        url: cached.url,
        itemId: meta.itemId,
        dataType: meta.dataType,
        fromCache: true,
        resolvedAt: new Date(cached.resolvedAt).toISOString(),
        compressionMethod: cached.compressionMethod,
        wasDecompressed: cached.wasDecompressed,
        thumbnailUrl: meta.thumbnailUrl,
        hlsMasterUrl: meta.hlsMasterUrl,
      });
    }

    // 2. Deduplicate: if the exact same item is already in-flight, return same promise
    const inflightKey = String(meta.itemId);
    const existing = this.inflightRequests.get(inflightKey);
    if (existing) {
      this.log(`Deduplicated: ${inflightKey}`);
      return existing;
    }

    // 3. Enqueue and process
    const promise = new Promise<CDNResolvedResource>((resolve, reject) => {
      const priority = options.priority ?? 0;
      this.queue.push({
        meta,
        options,
        resolve,
        reject: (err) => reject(err),
        priority,
        enqueuedAt: Date.now(),
      });
      // Keep queue sorted by priority DESC (higher = sooner)
      this.queue.sort((a, b) => b.priority - a.priority);
    });

    this.inflightRequests.set(inflightKey, promise);

    // Always clean up inflight map
    promise
      .catch(() => { })
      .finally(() => this.inflightRequests.delete(inflightKey));

    // Kick the queue
    this.processQueue();

    return promise;
  }

  /**
   * Fetch many items at once. Returns a Map of results as they resolve.
   * Uses the priority queue internally. First items in the array get higher priority.
   *
   * @param items  Array of CDNItemMeta (e.g. 5000 questions)
   * @param options Shared options applied to all items
   * @param onEach  Optional callback fired each time an item resolves or fails
   */
  async fetchBatch(
    items: CDNItemMeta[],
    options: CDNFetchOptions = {},
    onEach?: (itemId: string | number, result: CDNResolvedResource | CDNFetchError, progress: number) => void,
  ): Promise<Map<string | number, CDNResolvedResource | CDNFetchError>> {
    const results = new Map<string | number, CDNResolvedResource | CDNFetchError>();
    let completed = 0;
    const total = items.length;

    const promises = items.map((meta, index) => {
      // Items earlier in the array get higher priority
      const priority = options.priority ?? (total - index);

      return this.fetch(meta, { ...options, priority })
        .then((res) => {
          results.set(meta.itemId, res);
          completed++;
          onEach?.(meta.itemId, res, completed / total);
          this.notifyListeners(meta.itemId, res);
        })
        .catch((err: CDNFetchError) => {
          const fetchError: CDNFetchError = {
            itemId: meta.itemId,
            message: err?.message || 'Unknown error',
            status: err?.status ?? -1,
            fallbackUrl: options.fallbackUrl,
          };
          results.set(meta.itemId, fetchError);
          completed++;
          onEach?.(meta.itemId, fetchError, completed / total);
          this.notifyListeners(meta.itemId, fetchError);
        });
    });

    await Promise.allSettled(promises);
    return results;
  }

  /**
   * Build the full CDN URL for a public item.
   * If cdnPath is already a full URL (http/https/protocol-relative), returns it
   * as-is to avoid double-prepend when Question_Image or file_Url contains a
   * full URL from Django or another storage host.
   * Useful when you just need the URL string without fetching.
   */
  buildCDNUrl(cdnPath: string, version?: string | number): string {
    // Full URL passthrough — handles Django media URLs, absolute CDN URLs, and local data/blob URIs
    if (cdnPath.startsWith('http://') || cdnPath.startsWith('https://') || cdnPath.startsWith('//') || cdnPath.startsWith('data:') || cdnPath.startsWith('blob:')) {
      // Don't append version to data: or blob: URIs
      if (cdnPath.startsWith('data:') || cdnPath.startsWith('blob:')) return cdnPath;
      
      const sep = cdnPath.includes('?') ? '&' : '?';
      return version != null ? `${cdnPath}${sep}v=${version}` : cdnPath;
    }
    const base = this.config.cdnBaseUrl.replace(/\/$/, '');
    const path = cdnPath.startsWith('/') ? cdnPath : `/${cdnPath}`;
    const versionSuffix = version != null ? `?v=${version}` : '';
    return `${base}${path}${versionSuffix}`;
  }

  /**
   * Manually invalidate a cached item (e.g. after content update).
   * Revokes blob: URL if applicable.
   */
  invalidate(itemId: string | number): void {
    const key = String(itemId);
    const entry = this.cache.get(key);
    if (entry?.isBlobUrl && entry.url.startsWith('blob:')) {
      try { URL.revokeObjectURL(entry.url); } catch { /* ignore */ }
    }
    this.cache.delete(key);
    this.cacheAccessOrder = this.cacheAccessOrder.filter((k) => k !== key);
    this.log(`Invalidated cache: ${key}`);
  }

  /** Clear the entire cache. Revokes all blob: URLs to prevent memory leaks. */
  clearCache(): void {
    for (const entry of this.cache.values()) {
      if (entry.isBlobUrl && entry.url.startsWith('blob:')) {
        try { URL.revokeObjectURL(entry.url); } catch { /* ignore */ }
      }
    }
    this.cache.clear();
    this.cacheAccessOrder = [];
    this.log('Cache cleared');
  }

  /** Get cache statistics */
  getCacheStats() {
    let hitCount = 0;
    let expiredCount = 0;
    for (const entry of this.cache.values()) {
      if (this.isCacheExpired(entry)) expiredCount++;
      else hitCount++;
    }
    return {
      totalEntries: this.cache.size,
      validEntries: hitCount,
      expiredEntries: expiredCount,
      queueLength: this.queue.length,
      activeRequests: this.activeCount,
    };
  }

  /** Subscribe to individual item resolution (used by useCDNBatch hook) */
  subscribe(
    listener: (itemId: string | number, result: CDNResolvedResource | CDNFetchError) => void,
  ): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /** Update config at runtime */
  configure(config: Partial<CDNServiceConfig>): void {
    this.config = { ...this.config, ...config };
    this.log('Config updated');
  }

  // ────────────────────────────────────────────
  // Static path-inference helpers
  // ────────────────────────────────────────────

  /**
   * Infer compression method from the file path extension.
   * Backend often omits compression_method so we derive it here.
   *   local/q/xxx_data.json.gz  → 'gzip'
   *   images/foo.webp           → 'webp'
   *   anything else             → 'none'
   */
  static inferCompressionFromPath(path: string): CompressionMethod {
    const lower = path.toLowerCase().split('?')[0];
    if (lower.endsWith('.gz') || lower.endsWith('.gzip')) return 'gzip';
    if (lower.endsWith('.webp')) return 'webp';
    return 'none';
  }

  /**
   * Infer CDNDataType from the file path extension.
   * Strips a trailing .gz/.gzip suffix first so .json.gz → 'json'.
   */
  static inferDataTypeFromPath(path: string): CDNDataType {
    const lower = path.toLowerCase().split('?')[0].replace(/\.gz$|\.gzip$/, '');
    if (/\.(png|jpg|jpeg|webp|gif|svg|ico|bmp)$/.test(lower)) return 'image';
    if (/\.(mp4|webm|ogv|mov|avi)$/.test(lower)) return 'video';
    if (/\.(mp3|wav|ogg|aac|flac)$/.test(lower)) return 'audio';
    if (/\.pdf$/.test(lower)) return 'pdf';
    if (/\.json$/.test(lower)) return 'json';
    if (/\.(html|htm)$/.test(lower)) return 'html';
    if (/\.txt$/.test(lower)) return 'text';
    return 'other';
  }

  // ────────────────────────────────────────────
  // Queue Processing
  // ────────────────────────────────────────────

  private processQueue(): void {
    while (this.activeCount < this.config.maxConcurrency && this.queue.length > 0) {
      const item = this.queue.shift();
      if (!item) break;
      this.activeCount++;
      this.executeItem(item).finally(() => {
        this.activeCount--;
        this.processQueue();
      });
    }
  }

  private async executeItem(item: CDNQueueItem): Promise<void> {
    const { meta, options, resolve, reject } = item;

    try {
      let resolvedUrl: string;
      let wasDecompressed = false;

      if (meta.isPublic) {
        resolvedUrl = await this.resolvePublicUrl(meta, options);
      } else {
        resolvedUrl = await this.resolvePrivateUrl(meta, options);
      }

      // ── Decompression step (gzip only — browser handles webp/video/audio natively) ──
      // Skip if skipDecompression flag is set (user wants raw compressed data)
      if (this.needsDecompression(meta.compressionMethod) && !options.skipDecompression) {
        this.log(`Decompressing gzip: ${meta.itemId}`);
        resolvedUrl = await this.decompressAndCreateBlobUrl(resolvedUrl, meta, options);
        wasDecompressed = true;
      } else if (this.needsDecompression(meta.compressionMethod) && options.skipDecompression) {
        this.log(`Skipping decompression (user requested raw data): ${meta.itemId}`);
      }

      const result: CDNResolvedResource = {
        url: resolvedUrl,
        itemId: meta.itemId,
        dataType: meta.dataType,
        fromCache: false,
        resolvedAt: new Date().toISOString(),
        compressionMethod: meta.compressionMethod,
        wasDecompressed,
        thumbnailUrl: meta.thumbnailUrl,
        hlsMasterUrl: meta.hlsMasterUrl,
      };

      // Cache the result
      this.setCacheEntry(meta, resolvedUrl, wasDecompressed);

      resolve(result);
    } catch (err: any) {
      const cdnUrl = this.buildCDNUrl(meta.cdnPath, meta.version);
      this.logError('fetch failed', meta.itemId, cdnUrl, err);
      const fetchError: CDNFetchError = {
        itemId: meta.itemId,
        message: err?.message || 'Failed to fetch CDN resource',
        status: err?.response?.status ?? err?.status ?? -1,
        fallbackUrl: options.fallbackUrl,
      };
      reject(fetchError);
    }
  }

  // ────────────────────────────────────────────
  // URL Resolution
  // ────────────────────────────────────────────

  /**
   * PUBLIC item flow:
   * Files are stored in R2 and served directly via CDN.
   * No HEAD check or re-push is needed — just build the full CDN URL
   * from the relative path and return it. The CDN/R2 is the source of truth.
   *
   * Non-CDN full URLs (Django media, external hosts) are passed through as-is.
   */
  private async resolvePublicUrl(meta: CDNItemMeta, options: CDNFetchOptions): Promise<string> {
    const cdnUrl = this.buildCDNUrl(meta.cdnPath, meta.version);

    // Non-CDN full URL — return directly without any CDN processing
    const isCDNHost = cdnUrl.startsWith(this.config.cdnBaseUrl);
    if (!isCDNHost) {
      this.log(`Non-CDN URL, using directly: ${cdnUrl}`);
      return cdnUrl;
    }

    // R2/CDN-hosted file — return URL directly, no HEAD check needed.
    // Decompression (if compressionMethod=gzip) runs next in executeItem.
    this.log(`CDN R2 direct: ${cdnUrl}`);
    return cdnUrl;
  }

  /**
   * Call backend to re-push a public item to CDN.
   * Backend should push the file from R2 → CDN and return the fresh path.
   */
  private async requestPublicRepush(
    meta: CDNItemMeta,
    options: CDNFetchOptions,
  ): Promise<string> {
    const endpoint = options.apiEndpoint || this.config.defaultPublicApiEndpoint;
    const apiBase = this.config.apiBaseUrl || baseURL;
    const retries = options.maxRetries ?? 1;
    let lastError: any;

    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        const response = await axios.post(
          `${apiBase}${endpoint}`,
          { item_id: meta.itemId },
          {
            timeout: options.timeout ?? 15_000,
            headers: options.headers,
            signal: options.signal,
          },
        );

        const data = response.data;
        // Backend returns { cdn_path, version } or similar
        const newPath = data.cdn_path || data.cdnPath || meta.cdnPath;
        const newVersion = data.version ?? meta.version;
        return this.buildCDNUrl(newPath, newVersion);
      } catch (err) {
        lastError = err;
        if (attempt < retries) {
          // Exponential backoff: 500ms, 1000ms, ...
          await this.delay(500 * Math.pow(2, attempt));
        }
      }
    }

    throw lastError;
  }

  /**
   * PRIVATE item flow:
   * 1. Call backend API to get a short-lived signed URL
   * 2. Return the signed URL directly (NO caching by default)
   */
  private async resolvePrivateUrl(meta: CDNItemMeta, options: CDNFetchOptions): Promise<string> {
    const endpoint = options.apiEndpoint || this.config.defaultPrivateApiEndpoint;
    const apiBase = this.config.apiBaseUrl || baseURL;

    const response = await axios.get(
      `${apiBase}${endpoint}/${meta.itemId}`,
      {
        timeout: options.timeout ?? 15_000,
        headers: options.headers,
        signal: options.signal,
      },
    );

    const data = response.data;
    const signedUrl = data.signed_url || data.signedUrl || data.url;

    if (!signedUrl) {
      throw new Error(`No signed URL returned for item ${meta.itemId}`);
    }

    this.log(`Signed URL received: ${meta.itemId}`);
    return signedUrl;
  }

  // ────────────────────────────────────────────
  // Decompression (gzip → blob URL)
  // ────────────────────────────────────────────

  /**
   * Only gzip needs manual decompression.
   * webp/video/audio are format-level compressions handled natively by browsers.
   */
  private needsDecompression(method?: CompressionMethod): boolean {
    return method === 'gzip';
  }

  /**
   * Fetch the resource as binary, decompress gzip, create a blob: URL.
   *
   * Strategy:
   *  1. Try DecompressionStream API (modern browsers, Chrome 80+, Android Chrome auto-updates)
   *  2. Fallback: let Response handle it (some servers set Content-Encoding: gzip)
   *  3. Last resort: return raw URL and let the browser attempt it
   *
   * The blob: URL is cached and revoked on eviction to prevent memory leaks.
   */
  private async decompressAndCreateBlobUrl(
    url: string,
    meta: CDNItemMeta,
    options: CDNFetchOptions,
  ): Promise<string> {
    try {
      const response = await fetch(url, {
        signal: options.signal ?? AbortSignal.timeout(options.timeout ?? 30_000),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status} fetching ${url}`);
      }

      // Determine MIME type for the blob
      const mimeType = meta.mimeType || this.inferMimeType(meta.dataType, meta.cdnPath);

      // Step 1: Decompress
      let decompressedBuffer: ArrayBuffer;

      // Try DecompressionStream (available in Chrome 80+, Edge 80+, Safari 16.4+)
      if (typeof DecompressionStream !== 'undefined' && response.body) {
        // Clone response BEFORE consuming body — fallback needs an unconsumed body
        const fallbackResponse = response.clone();
        try {
          const decompressedStream = response.body.pipeThrough(new DecompressionStream('gzip'));
          const decompressedResponse = new Response(decompressedStream);
          decompressedBuffer = await decompressedResponse.arrayBuffer();
          this.log(`Decompressed via DecompressionStream: ${meta.itemId} (${decompressedBuffer.byteLength} bytes)`);
        } catch (streamErr) {
          console.warn(`[CDN] ⚠️ DecompressionStream failed for id=${meta.itemId}, trying fallback:`, streamErr);
          // Use the cloned response since the original body stream is now consumed
          decompressedBuffer = await fallbackResponse.arrayBuffer();
        }
      } else {
        // Fallback: Read as ArrayBuffer directly
        // (If the server set Content-Encoding: gzip, browser already decompressed it)
        decompressedBuffer = await response.arrayBuffer();
        this.log(`Decompressed via fallback: ${meta.itemId} (${decompressedBuffer.byteLength} bytes)`);
      }

      // Step 2: Apply unmasking if provided
      if (options.unmaskFunction) {
        this.log(`Unmasking data for: ${meta.itemId}`);
        decompressedBuffer = await options.unmaskFunction(decompressedBuffer);
        this.log(`Unmasked: ${meta.itemId} (${decompressedBuffer.byteLength} bytes)`);
      }

      // Step 3: Create blob: URL with unmasked data
      const blob = new Blob([decompressedBuffer], { type: mimeType });
      const blobUrl = URL.createObjectURL(blob);
      return blobUrl;
    } catch (err) {
      // Throw so the error propagates to executeItem → logError → visible in console
      this.logError('decompression/fetch failed', meta.itemId, url, err);
      throw err;
    }
  }

  /**
   * Infer MIME type from dataType and file extension.
   * Used to create properly-typed blobs after decompression.
   */
  private inferMimeType(dataType: string, cdnPath: string): string {
    // Strip trailing .gz/.gzip so xxx.json.gz → check 'json', not 'gz'
    const stripped = cdnPath.toLowerCase().split('?')[0].replace(/\.gz$|\.gzip$/, '');
    const ext = stripped.split('.').pop() ?? '';
    const extMap: Record<string, string> = {
      png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', webp: 'image/webp',
      gif: 'image/gif', svg: 'image/svg+xml', ico: 'image/x-icon', bmp: 'image/bmp',
      mp4: 'video/mp4', webm: 'video/webm', ogv: 'video/ogg', mov: 'video/quicktime',
      mp3: 'audio/mpeg', wav: 'audio/wav', ogg: 'audio/ogg', aac: 'audio/aac', flac: 'audio/flac',
      pdf: 'application/pdf', json: 'application/json', xml: 'application/xml',
      html: 'text/html', css: 'text/css', js: 'application/javascript',
      txt: 'text/plain', csv: 'text/csv', md: 'text/markdown',
      zip: 'application/zip',
      // gz removed intentionally — .gz is always stripped above before lookup
    };
    if (extMap[ext]) return extMap[ext];

    // Fallback by dataType
    const typeMap: Record<string, string> = {
      image: 'image/png', video: 'video/mp4', audio: 'audio/mpeg',
      pdf: 'application/pdf', json: 'application/json', html: 'text/html',
      text: 'text/plain', scorm: 'application/zip', binary: 'application/octet-stream',
    };
    return typeMap[dataType] || 'application/octet-stream';
  }

  // ────────────────────────────────────────────
  // Cache Management (LRU)
  // ────────────────────────────────────────────

  private buildCacheKey(meta: CDNItemMeta): string {
    return String(meta.itemId);
  }

  private isCacheExpired(entry: CDNCacheEntry): boolean {
    if (entry.expiresAt === null) return false;
    return Date.now() > entry.expiresAt;
  }

  private setCacheEntry(meta: CDNItemMeta, url: string, wasDecompressed = false): void {
    const key = this.buildCacheKey(meta);
    const ttl = meta.isPublic ? this.config.publicCacheTTL : this.config.privateCacheTTL;

    // Don't cache if TTL is 0 (e.g. private signed URLs)
    if (ttl === 0) return;

    // LRU eviction
    if (this.cache.size >= this.config.maxCacheSize) {
      this.evictOldest();
    }

    this.cache.set(key, {
      url,
      dataType: meta.dataType,
      resolvedAt: Date.now(),
      expiresAt: ttl > 0 ? Date.now() + ttl : null,
      isPublic: meta.isPublic,
      compressionMethod: meta.compressionMethod,
      wasDecompressed,
      isBlobUrl: url.startsWith('blob:'),
    });

    this.touchCacheKey(key);
  }

  private touchCacheKey(key: string): void {
    this.cacheAccessOrder = this.cacheAccessOrder.filter((k) => k !== key);
    this.cacheAccessOrder.push(key);
  }

  private evictOldest(): void {
    // Remove the 10% least recently used items
    const evictCount = Math.max(1, Math.floor(this.config.maxCacheSize * 0.1));
    const keysToEvict = this.cacheAccessOrder.splice(0, evictCount);
    for (const key of keysToEvict) {
      const entry = this.cache.get(key);
      // Revoke blob: URLs to free memory (important for decompressed data)
      if (entry?.isBlobUrl && entry.url.startsWith('blob:')) {
        try { URL.revokeObjectURL(entry.url); } catch { /* ignore */ }
      }
      this.cache.delete(key);
    }
    this.log(`Evicted ${keysToEvict.length} cache entries`);
  }

  // ────────────────────────────────────────────
  // Helpers
  // ────────────────────────────────────────────

  private notifyListeners(
    itemId: string | number,
    result: CDNResolvedResource | CDNFetchError,
  ): void {
    for (const listener of this.listeners) {
      try {
        listener(itemId, result);
      } catch {
        // Don't let a bad listener break the service
      }
    }
  }

  private delay(ms: number): Promise<void> {
    return new Promise((res) => setTimeout(res, ms));
  }

  private log(message: string): void {
    if (this.config.debug) {
      console.log(`[CDN] ${message}`);
    }
  }

  /**
   * Always-visible error logger — used for real failures regardless of debug flag.
   * CORS failures arrive as TypeError("Failed to fetch") with no response status.
   */
  private logError(context: string, itemId: string | number, url: string, err: any): void {
    const isCORS =
      err instanceof TypeError &&
      (err.message === 'Failed to fetch' || err.message.includes('CORS') || err.message.includes('NetworkError'));

    if (isCORS) {
      console.error(
        `[CDN] ❌ CORS BLOCKED — id=${itemId}\n` +
        `  URL: ${url}\n` +
        `  Fix: Add "Access-Control-Allow-Origin: *" (or your domain) to the R2 bucket CORS rules.\n` +
        `  R2 CORS config path: Cloudflare Dashboard → R2 → Bucket → Settings → CORS Policy`,
      );
    } else {
      console.error(`[CDN] ❌ ${context} — id=${itemId} url=${url}`, err);
    }
  }
}

// ════════════════════════════════════════════════════════════════
// Generic Decompression Utilities — Use anywhere in the app
// ════════════════════════════════════════════════════════════════

/**
 * Generic gzip decompression utility.
 * Fetches a .gz file from a URL and returns the decompressed data.
 * 
 * @param url - URL to the gzipped file (.json.gz, .txt.gz, etc.)
 * @param options - Optional fetch configuration
 * @returns Decompressed data as ArrayBuffer
 * 
 * @example
 * const buffer = await decompressGzip('https://cdn.com/data.json.gz');
 * const text = new TextDecoder().decode(buffer);
 * const json = JSON.parse(text);
 */
export async function decompressGzip(
  url: string,
  options?: { timeout?: number; signal?: AbortSignal }
): Promise<ArrayBuffer> {
  const response = await fetch(url, {
    signal:
      options?.signal ??
      (options?.timeout
        ? AbortSignal.timeout(options.timeout)
        : undefined),
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status} fetching ${url}`);
  }

  const buffer = await response.arrayBuffer();

  const isGzip = (bytes: Uint8Array) =>
    bytes.length >= 3 &&
    bytes[0] === 0x1f &&
    bytes[1] === 0x8b &&
    bytes[2] === 0x08;

  if (typeof DecompressionStream === "undefined") {
    // console.warn("DecompressionStream is not supported in this environment");
    return buffer;
  }

  let result = buffer;
  let level = 0;

  for (let i = 0; i < 3; i++) {
    const bytes = new Uint8Array(result);


    if (!isGzip(bytes)) {
      break;
    }

    // console.log("REAL GZIP DETECTED at Level", level, url);

    const stream = new Blob([result]).stream();
    const decompressedStream = stream.pipeThrough(
      new DecompressionStream("gzip")
    );

    result = await new Response(decompressedStream).arrayBuffer();
    level++;
  }

  if (level > 0) {
    try {
      const text = new TextDecoder().decode(result);
      // console.log(
      //   "AFTER DECOMPRESS",
      //   text.substring(0, 100)
      // );
    } catch (e) {
      // console.log("AFTER DECOMPRESS: binary data or decoding failed");
    }
  }

  return result;
}
/**
 * Fetch and decompress a gzipped file, returning it as text.
 * 
 * @param url - URL to the gzipped file
 * @param options - Optional fetch configuration
 * @returns Decompressed text content
 * 
 * @example
 * const jsonText = await decompressGzipAsText('https://cdn.com/data.json.gz');
 * const data = JSON.parse(jsonText);
 */
export async function decompressGzipAsText(
  url: string,
  options?: { timeout?: number; signal?: AbortSignal }
): Promise<string> {
  const buffer = await decompressGzip(url, options);
  return new TextDecoder('utf-8').decode(buffer);
}

/**
 * Fetch, decompress, and parse a gzipped JSON file.
 * 
 * @param url - URL to the .json.gz file
 * @param options - Optional fetch configuration
 * @returns Parsed JSON object
 * 
 * @example
 * const questionData = await decompressGzipAsJson('https://cdn.com/question.json.gz');
 * console.log(questionData.question_text);
 */
export async function decompressGzipAsJson<T = any>(
  url: string,
  options?: { timeout?: number; signal?: AbortSignal }
): Promise<T> {
  const text = await decompressGzipAsText(url, options);
  return JSON.parse(text) as T;
}

// ════════════════════════════════════════════════════════════════
// Singleton export — use this throughout the app
// ════════════════════════════════════════════════════════════════

export async function resolveCDNContent(cdnPath: string | null | undefined) {
  if (!cdnPath) return null;

  try {
    // If already full URL, don't rebuild
    const fullUrl = cdnPath.startsWith("http")
      ? cdnPath
      : cdnService.buildCDNUrl(cdnPath);

    // ✅ Strip query params before checking extension
    const cleanPath = fullUrl.split("?")[0].toLowerCase();

    if (cleanPath.endsWith(".gz") || cleanPath.endsWith(".gzip")) {
      return await decompressGzipAsJson(fullUrl);
    }

    if (cleanPath.endsWith(".json")) {
      const res = await fetch(fullUrl);
      if (!res.ok) return null;
      return await res.json();
    }

    const res = await fetch(fullUrl);
    if (!res.ok) return null;
    return await res.text();

  } catch (err) {
    console.warn("[CDN] resolve failed:", cdnPath, err);
    return null;
  }
}
export const cdnService = new CDNService();

// Also export the class for testing or creating additional instances
export { CDNService };
